var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useContext, useCallback } from 'react';
import cloneDeep from 'clone-deep';
import { useQueryClient } from 'react-query';
import OptimistContext from '../OptimistContext';
import OptimistError from './OptimistError';
import { OptimistUpdaterResult } from '.';
export const buildNoCachedDataWarning = (queryKey) => `Optimist: Nothing is saved in the queryCache for ${JSON.stringify(queryKey)}. This is a no-op.`;
const useReactQueryMutationOptions = (updaters, optimistOptions) => {
    var _a;
    const _b = (_a = optimistOptions === null || optimistOptions === void 0 ? void 0 : optimistOptions.globalMutationOptions) !== null && _a !== void 0 ? _a : {}, { onMutate: globalOnMutate, onError: globalOnError, onSettled: globalOnSettled, onSuccess: globalOnSuccess } = _b, otherGlobalOptions = __rest(_b, ["onMutate", "onError", "onSettled", "onSuccess"]);
    const queryClient = useQueryClient();
    const optimistContext = useContext(OptimistContext);
    if (!optimistContext) {
        throw new OptimistError('Optimist must be surrounded by OptimistProvider');
    }
    const { setPreviousQueryState, getPreviousQueryState } = optimistContext;
    const updateQueryCacheState = useCallback(function (queryKey, previousQueryState, updateCallback, variables) {
        if (!updateCallback) {
            console.warn(`Optimist: No updater provided for ${JSON.stringify(queryKey)}. This is a no-op.`);
            return;
        }
        const clonedQueryState = cloneDeep(previousQueryState);
        setPreviousQueryState(queryKey, clonedQueryState);
        return updateCallback(previousQueryState, variables);
    }, [setPreviousQueryState]);
    const undoUpdate = useCallback((queryKey) => {
        const previousState = getPreviousQueryState(queryKey);
        console.log(`Optimist: Experienced an error while mutating, rolling back queryKey: ${JSON.stringify(queryKey)}`);
        if (previousState) {
            queryClient.setQueryData(queryKey, previousState);
        }
    }, [getPreviousQueryState, queryClient]);
    const onMutate = useCallback((variables) => {
        var _a, _b;
        const context = {
            updaterResults: {}
        };
        for (const updater of updaters) {
            try {
                if (!(updater === null || updater === void 0 ? void 0 : updater.queryKey)) {
                    throw new OptimistError('OptimistUpdater must contain a query key');
                }
                const serializedQueryKey = JSON.stringify(updater.queryKey);
                queryClient.setQueryData(updater.queryKey, (previousQueryState) => {
                    if (!previousQueryState) {
                        console.warn(buildNoCachedDataWarning(updater.queryKey));
                        context.updaterResults[serializedQueryKey] = OptimistUpdaterResult.NotFound;
                        return;
                    }
                    return updateQueryCacheState(updater.queryKey, previousQueryState, updater === null || updater === void 0 ? void 0 : updater.updateCallback, variables);
                });
                if (((_a = context.updaterResults) === null || _a === void 0 ? void 0 : _a[serializedQueryKey]) !== OptimistUpdaterResult.NotFound) {
                    context.updaterResults[serializedQueryKey] = OptimistUpdaterResult.Success;
                }
                if ((_b = updater === null || updater === void 0 ? void 0 : updater.options) === null || _b === void 0 ? void 0 : _b.onMutate)
                    updater.options.onMutate(variables);
            }
            catch (error) {
                console.error(error);
                undoUpdate(updater === null || updater === void 0 ? void 0 : updater.queryKey);
                console.error(`The above error was Optimist having an error during updating queryKey: ${JSON.stringify(updater === null || updater === void 0 ? void 0 : updater.queryKey)}, use onOptimistError to diagnose or act. Rolling back to previous state...`);
                const serializedQueryKey = JSON.stringify(updater.queryKey);
                context.updaterResults[serializedQueryKey] = OptimistUpdaterResult.Error;
                if (optimistOptions === null || optimistOptions === void 0 ? void 0 : optimistOptions.onOptimistError)
                    optimistOptions.onOptimistError(error, variables);
            }
        }
        try {
            if (globalOnMutate) {
                const contextResponse = globalOnMutate(variables);
                Object.assign(context, contextResponse);
            }
        }
        catch (error) {
            console.error('Optimist had an error while executing the global onMutation, but it was caught to not prevent the mutation:', error);
        }
        return context;
    }, [updaters, updateQueryCacheState, optimistOptions, queryClient, undoUpdate, globalOnMutate]);
    const onError = useCallback((error, variables, context) => {
        var _a;
        for (const updater of updaters) {
            try {
                if (updater === null || updater === void 0 ? void 0 : updater.queryKey)
                    undoUpdate(updater === null || updater === void 0 ? void 0 : updater.queryKey);
                if ((_a = updater === null || updater === void 0 ? void 0 : updater.options) === null || _a === void 0 ? void 0 : _a.onError)
                    updater.options.onError(error, variables, undefined);
            }
            catch (innerError) {
                if (optimistOptions === null || optimistOptions === void 0 ? void 0 : optimistOptions.onOptimistError)
                    optimistOptions.onOptimistError(innerError, variables);
            }
        }
        try {
            if (globalOnError)
                globalOnError(error, variables, context);
        }
        catch (globalError) {
            console.error('Optimist had an error while executing the global onError, but it was caught to not prevent the mutation:', globalError);
        }
    }, [updaters, undoUpdate, optimistOptions, globalOnError]);
    const onSettled = useCallback((data, error, variables, context) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d, _e;
        for (const updater of updaters) {
            try {
                if ((updater === null || updater === void 0 ? void 0 : updater.queryKey) && ((_c = updater === null || updater === void 0 ? void 0 : updater.options) === null || _c === void 0 ? void 0 : _c.refetchQuery) !== false) {
                    const updaterResult = (_d = context === null || context === void 0 ? void 0 : context.updaterResults) === null || _d === void 0 ? void 0 : _d[JSON.stringify(updater.queryKey)];
                    if (updaterResult !== OptimistUpdaterResult.NotFound) {
                        queryClient.refetchQueries(updater.queryKey);
                    }
                }
                if ((_e = updater === null || updater === void 0 ? void 0 : updater.options) === null || _e === void 0 ? void 0 : _e.onSettled)
                    updater.options.onSettled(data, error, variables, undefined);
            }
            catch (innerError) {
                if (optimistOptions === null || optimistOptions === void 0 ? void 0 : optimistOptions.onOptimistError)
                    optimistOptions.onOptimistError(innerError, variables);
            }
        }
        try {
            if (globalOnSettled)
                globalOnSettled(data, error, variables, context);
        }
        catch (globalError) {
            console.error('Optimist had an error while executing the global onSettled, but it was caught to not prevent the mutation:', globalError);
        }
    }), [updaters, queryClient, optimistOptions, globalOnSettled]);
    const onSuccess = useCallback((data, variables, context) => {
        var _a;
        for (const updater of updaters) {
            try {
                if ((_a = updater === null || updater === void 0 ? void 0 : updater.options) === null || _a === void 0 ? void 0 : _a.onSuccess)
                    updater.options.onSuccess(data, variables, undefined);
            }
            catch (innerError) {
                if (optimistOptions === null || optimistOptions === void 0 ? void 0 : optimistOptions.onOptimistError)
                    optimistOptions.onOptimistError(innerError, variables);
            }
        }
        try {
            if (globalOnSuccess)
                globalOnSuccess(data, variables, context);
        }
        catch (error) {
            console.error('Optimist had an error while executing the global onSuccess:', error);
        }
    }, [updaters, optimistOptions, globalOnSuccess]);
    return Object.assign({ onMutate, onError, onSettled, onSuccess }, otherGlobalOptions);
};
export default useReactQueryMutationOptions;
//# sourceMappingURL=useReactQueryMutationOptions.js.map